import { useEffect, useMemo, useState } from "react";
import { Eye, Filter, RotateCcw, UserRoundPlus } from "lucide-react";

import { EmployeeTableAvatar } from "../../components/features/approve-bank-details/components/employee-table-avatar.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Badge, Button, DateRange, ResponsiveDataTable, Select, Spinner, type BadgeVariant, type ResponsiveColumn } from "../../components/ui/index.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import type { PageProps } from "../../types/page.ts";
import { formatToLongDate } from "../../utils/helpers.ts";
import { showError } from "../../utils/toast.ts";
import { ProjectEnrollmentRequestDetailModal } from "./project-enrollment-request-detail-modal.tsx";
import type { EnrollmentRequestFilters, EnrollmentRequestRow, EnrollmentRequestStatus } from "./project-enrollment-requests-types.ts";
import { useProjectEnrollmentRequests } from "./use-project-enrollment-requests.ts";
import styles from "./project-enrollment-requests-page.module.css";

const EMPTY_FILTERS: EnrollmentRequestFilters = { employeeId: "", status: "", startDate: null, endDate: null };
const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

function statusVariant(status: EnrollmentRequestStatus): BadgeVariant {
  if (status === "APPROVED") return "success";
  if (status === "REJECTED" || status === "CANCELLED") return "danger";
  return "warning";
}

export default function ProjectEnrollmentRequestsPage({ title, pageMeta }: PageProps) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const employees = useEmployeeNameOptions();
  const pageTitle = pageMeta?.title || title || "Project Enrollment Requests";
  const [draftFilters, setDraftFilters] = useState<EnrollmentRequestFilters>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<EnrollmentRequestFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const list = useProjectEnrollmentRequests(filters, page, pageSize);

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  useEffect(() => {
    if (list.error) showError(list.error);
  }, [list.error]);

  useEffect(() => {
    if (employees.error) showError(employees.error);
  }, [employees.error]);

  const columns = useMemo<ResponsiveColumn<EnrollmentRequestRow>[]>(() => [
    { id: "serial", header: "#", render: (_row, index) => (page - 1) * pageSize + index + 1, nowrap: true },
    { id: "employeeName", header: "Employee Name", accessor: "employeeName", render: (row) => <div className={styles.employeeCell}><EmployeeTableAvatar employeeId={row.employeeId} name={row.employeeName} photoPath={row.photoPath ?? undefined} size="sm" /><span><strong>{row.employeeName}</strong>{row.employeeCode ? <small>{row.employeeCode}</small> : null}</span></div> },
    { id: "designation", header: "Designation", accessor: "designation", truncate: true },
    { id: "createdAt", header: "Request Date", accessor: "createdAt", render: (row) => formatToLongDate(row.createdAt, "—"), nowrap: true },
    { id: "status", header: "Status", accessor: "status", render: (row) => <Badge variant={statusVariant(row.status)} dot>{row.status.charAt(0) + row.status.slice(1).toLowerCase()}</Badge>, nowrap: true },
    { id: "action", header: "Action", render: (row) => <Button size="sm" variant="accent-ghost" startIcon={<Eye size={16} />} onClick={() => setSelectedId(row.id)}>View</Button>, nowrap: true },
  ], [page, pageSize]);

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  return (
    <PageShell className={styles.page}>
      <Card className={styles.headingCard}>
        <span className={styles.headingIcon}><UserRoundPlus size={28} /></span>
        <div><h1>{pageTitle}</h1><p>Review employee requests to join a project and update their status.</p></div>
      </Card>
      <Card className={styles.filterCard}>
        <div className={styles.filters}>
          <Select id="enrollment-employee-filter" label="Employee Name" placeholder={employees.loading ? "Loading Employees..." : "Select Employee Name"} searchable isClearable options={employees.options} value={draftFilters.employeeId} disabled={employees.loading} onChange={(employeeId) => setDraftFilters((current) => ({ ...current, employeeId }))} />
          <Select id="enrollment-status" label="Status" placeholder="All Statuses" isClearable options={STATUS_OPTIONS} value={draftFilters.status} onChange={(status) => setDraftFilters((current) => ({ ...current, status }))} />
          <DateRange id="enrollment-date-range" label="Date Range" placeholderText="All Dates" startDate={draftFilters.startDate} endDate={draftFilters.endDate} onChange={([startDate, endDate]) => setDraftFilters((current) => ({ ...current, startDate, endDate }))} />
          <div className={styles.filterActions}>
            <Button startIcon={<Filter size={16} />} disabled={list.loading} onClick={() => { setFilters({ ...draftFilters }); setPage(1); }}>Apply Filters</Button>
            <Button variant="light-grey" startIcon={<RotateCcw size={16} />} disabled={list.loading} onClick={clearFilters}>Reset</Button>
          </div>
        </div>
      </Card>
      <Card padding="none" className={styles.tableCard}>
        {list.loading ? <div className={styles.loading}><Spinner size="lg" label="Loading enrollment requests" /></div> : <ResponsiveDataTable<EnrollmentRequestRow> aria-label="Project enrollment requests" columns={columns} data={list.rows} pagination page={page} pageSize={pageSize} totalItems={list.totalElements} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} pageSizeOptions={[5, 10, 20, 30, 50]} totalLabel={`Total Requests: ${list.totalElements}`} emptyState={<EmptyStateMessage message="No enrollment requests match the selected filters." />} />}
      </Card>
      <ProjectEnrollmentRequestDetailModal requestId={selectedId} onClose={() => setSelectedId(null)} onUpdated={list.refetch} />
    </PageShell>
  );
}
